'use client';

import React, { useRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { Input } from '../Input';
import { InputProps } from '@/types/components';
import { TimePickerIcon } from '@/components/icons';

export interface TimePickerProps extends Omit<InputProps, 'type' | 'onChange'> {
  /** Time value (HH:MM format) */
  value?: string;
  /** Callback when time changes */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Minimum time (HH:MM format) */
  min?: string;
  /** Maximum time (HH:MM format) */
  max?: string;
}

export interface TimePickerRef {
  showPicker: () => void;
}

const ClickableIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  pointer-events: auto;
  
  &:hover {
    opacity: 0.7;
  }
`;

/**
 * TimePicker Component
 * 
 * A simple time picker using native HTML5 time input with clickable clock icon
 * 
 * @component
 * @example
 * ```tsx
 * <TimePicker
 *   label="Start Time"
 *   value={startTime}
 *   onChange={(e) => setStartTime(e.target.value)}
 *   min="09:00"
 *   max="17:00"
 * />
 * ```
 */
export const TimePicker = React.forwardRef<TimePickerRef, TimePickerProps>(({
  value,
  onChange,
  min,
  max,
  id,
  onClick,
  ...inputProps
}, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const showPicker = () => {
    if (inputRef.current) {
      // Use showPicker() if available (modern browsers), otherwise fallback to click()
      if ('showPicker' in inputRef.current && typeof (inputRef.current as HTMLInputElement).showPicker === 'function') {
        (inputRef.current as HTMLInputElement).showPicker();
      } else {
        inputRef.current.click();
      }
    }
  };

  // Expose showPicker method via ref
  useImperativeHandle(ref, () => ({
    showPicker,
  }));

  const handleIconClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    showPicker();
  };

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    // Call onClick prop if provided
    onClick?.(e);
    // Open picker when input is clicked
    showPicker();
  };

  return (
    <Input
      ref={inputRef}
      type="time"
      value={value || ''}
      onChange={onChange}
      onClick={handleInputClick}
      id={id}
      endAdornment={
        <ClickableIconWrapper onClick={handleIconClick} role="button" aria-label="Open time picker">
          <TimePickerIcon size={20} />
        </ClickableIconWrapper>
      }
      {...(min && { min })}
      {...(max && { max })}
      {...inputProps}
    />
  );
});

TimePicker.displayName = 'TimePicker';

export default TimePicker;

